import type { DocsPageTranslation } from './types'

// Initial LLM-assisted Spanish docs translation (neutral international Spanish,
// tú voice). Localization team should review before release; source_en_hash
// marks the English source revision used here.
export const DOCS_PAGE_TRANSLATIONS_ES: Record<string, DocsPageTranslation> = {
  'getting-started/welcome': {
    source_en_hash: 'v1:1ehqbgkba04',
    title: 'Bienvenido',
    description: 'Qué es Martini y cómo lo usan los cineastas.',
    overview: `Martini es un set de rodaje para video con IA. Trabajas en un lienzo infinito: colocas tomas, generas con modelos de IA, iteras hasta que el resultado funcione y luego montas todo en una línea de tiempo. Está hecho para iterar rápido, organizar espacialmente y colaborar en tiempo real.

![](/docs-imgs/welcome1.mp4)

Pulsa ||V|| para colocar una toma. Escribe un prompt, elige un modelo y genera. Si la toma no es la adecuada, ajusta el prompt, cambia la imagen de referencia o prueba con otro modelo. Cada nueva toma queda al lado de la original para que puedas compararlas.

![](/docs-imgs/welcome2.mp4)

Cuando tengas las tomas que quieres, arrástralas a la línea de tiempo y exporta. Martini soporta Veo, Kling, Sora y otros modelos a través de una sola interfaz, así puedes elegir el modelo que mejor se adapta a cada toma.`,
  },
  'getting-started/first-shot': {
    source_en_hash: 'v1:1516v8f2dr5',
    title: 'Tu primera toma',
    description: 'Genera una imagen y conviértela en video.',
    overview: `Todo proyecto comienza con un lienzo en blanco. Pulsa ||V|| para colocar una toma, escribe un prompt, elige un modelo y genera. Ese es el flujo de trabajo central. El resto es iteración.

Martini soporta generación de imagen a video, video a video y texto a video. Elige el enfoque que mejor se adapte a la toma. Muchos cineastas empiezan con una imagen generada o subida y luego la usan como frame inicial para el video. La imagen ancla la composición; el modelo se encarga del movimiento.`,
  },
  'getting-started/iterating': {
    source_en_hash: 'v1:7e0ld19ccr',
    title: 'Iterar sobre las tomas',
    description: 'Pule las tomas mediante una iteración rápida.',
    overview: `La primera generación rara vez es la definitiva. Reescribe el prompt, cambia la imagen de referencia, prueba con otro modelo o ajusta la duración. Cada toma queda junto a la original para que puedas compararlas lado a lado.

Martini está construido alrededor de un ciclo simple: generar, revisar, ajustar, regenerar. El lienzo te da espacio para desplegar opciones. Las pilas mantienen juntas las tomas relacionadas cuando estás afinando una sola toma.`,
  },
  'canvas/overview': {
    source_en_hash: 'v1:24twlaut3dj',
    title: 'Resumen',
    description: 'El espacio de trabajo infinito donde vive todo.',
    overview: `El lienzo es un espacio de trabajo 2D infinito. Tu proyecto vive aquí: tomas, imágenes, audio, sets y anotaciones de texto. No hay páginas ni carpetas por las que navegar. Organizas espacialmente, como dispondrías selects sobre una mesa de luz o referencias en una pared.

Cada objeto del lienzo sigue las mismas reglas para selección, movimiento y organización. Aleja el zoom para ver todo el proyecto o acércalo para inspeccionar un frame. Desplaza para hacer zoom, arrastra con dos dedos para desplazarte y pulsa ||F|| para encajar todo en la vista.`,
  },
  'canvas/nodes': {
    source_en_hash: 'v1:1wj9a4wqesi',
    title: 'Objetos del lienzo',
    description: 'Cada tipo de objeto que puedes colocar en el lienzo.',
    overview: `Pulsa un atajo para entrar en modo de colocación, luego haz clic en el lienzo para colocar el objeto. Usa ||V|| para tomas de video, ||I|| para imágenes, ||T|| para anotaciones de texto, ||B|| para bins, ||Shift V|| para variables, ||Shift S|| para sets y ||A|| para audio.

Cada tipo tiene una función. Las tomas e imágenes contienen material visual. Las colecciones de la Library mantienen consistentes a los personajes y los props en todos tus proyectos. Las variables almacenan fragmentos de prompt reutilizables. Los bins agrupan trabajo relacionado. Las anotaciones son notas para ti o para tus colaboradores. Los sets son entornos 3D desde los que puedes generar tomas.`,
  },
  'canvas/selection': {
    source_en_hash: 'v1:aigbiuhqie',
    title: 'Selección',
    description: 'Haz clic, Shift+clic y marquesina para seleccionar trabajo en el lienzo.',
    overview: `Haz clic para seleccionar. ||Shift||+clic para añadir a la selección. Arrastra sobre un área vacía para seleccionar con marquesina. ||⌘A|| selecciona todo. ||Escape|| limpia la selección.

Los objetos seleccionados muestran un borde. La selección múltiple te permite mover, eliminar o etiquetar varios objetos a la vez. El inspector muestra los detalles del objeto seleccionado más recientemente.

Arrastra un objeto para moverlo sin cambiar su contenido. Si hay varios objetos seleccionados, arrastrar cualquiera de ellos mueve toda la selección y conserva sus posiciones relativas.`,
  },
  'canvas/organizing': {
    source_en_hash: 'v1:28jlsrfkc2l',
    title: 'Organización',
    description: 'Etiquetas de color, ratings, bins y filtros.',
    overview: `Pulsa de ||1|| a ||5|| para aplicar una etiqueta de color. ||F|| marca como favorita. ||R|| marca como rechazada. Son las herramientas de triaje que usan los editores cuando sacan selects de los dailies: marcan las tomas fuertes, rechazan las débiles y revisan lo que queda.

Pulsa ||B|| para crear un bin y luego arrastra tomas dentro para agruparlas en el lienzo. Los bins son contenedores, no carpetas; todo sigue visible y accesible. Pulsa ||Shift F|| para abrir filtros por favoritos, rechazadas o una etiqueta de color específica. Los filtros solo cambian lo que ves.`,
  },
  'canvas/clipboard': {
    source_en_hash: 'v1:19nz99iz0ro',
    title: 'Copiar, pegar y duplicar',
    description: 'Operaciones de portapapeles y duplicado rápido.',
    overview: `||⌘C|| copia, ||⌘V|| pega y ||⌘D|| duplica. Los objetos copiados se pegan cerca del original conservando sus ajustes. ||⌘D|| es más rápido cuando quieres una copia inmediata sin usar el portapapeles.

Estas operaciones funcionan sobre un solo objeto o una selección múltiple. Copia cinco tomas, pégalas en otra parte del lienzo y mantendrán sus posiciones relativas.`,
  },
  'canvas/undo-redo': {
    source_en_hash: 'v1:19hh43keqnm',
    title: 'Deshacer y rehacer',
    description: 'Revierte y restaura acciones del lienzo.',
    overview: `||⌘Z|| deshace la última acción. ||⌘ Shift Z|| la rehace. Deshacer cubre el trabajo del lienzo, como mover, eliminar, renombrar y crear objetos.

Deshacer es consciente de la colaboración. Revierte tus acciones, no las de tus colaboradores. Si alguien más mueve un nodo mientras trabajas, tu ||⌘Z|| no deshará ese movimiento.`,
  },
  'canvas/rename': {
    source_en_hash: 'v1:glr66svv1q',
    title: 'Renombrar',
    description: 'Renombra trabajo directamente en el lienzo.',
    overview: `Selecciona un objeto y pulsa ||Enter|| para renombrarlo en línea. Escribe el nuevo nombre y pulsa ||Enter|| de nuevo para confirmar, o ||Escape|| para cancelar. Los nombres aparecen debajo de los objetos en el lienzo y ayudan a identificar tomas cuando trabajas con muchas takes.`,
  },
  'canvas/delete': {
    source_en_hash: 'v1:8fw602mwuu',
    title: 'Eliminar',
    description: 'Elimina trabajo del lienzo.',
    overview: `Pulsa ||Delete|| o ||Backspace|| para eliminar los objetos seleccionados. Funciona sobre un solo objeto o sobre una selección múltiple. La eliminación se puede deshacer; ||⌘Z|| recupera el trabajo.`,
  },
  'canvas/playback': {
    source_en_hash: 'v1:1xs0lrqf04s',
    title: 'Reproducción',
    description: 'Previsualiza tomas y haz scrub en el video.',
    overview: `||Space|| reproduce y pausa. ||J||/||K||/||L|| hacen scrub: ||J|| reproduce hacia atrás, ||L|| reproduce hacia adelante y ||K|| detiene. Pulsa ||L|| dos veces para velocidad doble. Los controles coinciden con Premiere, Avid y DaVinci.

Selecciona una toma y pulsa ||Space|| para previsualizarla en su sitio dentro del lienzo. El program monitor sobre la línea de tiempo muestra el frame actual a mayor tamaño.`,
  },
  'generation/overview': {
    source_en_hash: 'v1:65l7fbi17r',
    title: 'Resumen',
    description: 'Cómo funciona la generación con IA en Martini.',
    overview: `Martini se conecta a varios modelos de IA: Veo, Kling, Sora y otros. No necesitas cuentas separadas ni API keys. Elige un modelo, escribe un prompt, ajusta los parámetros y genera. Cada resultado aparece en el lienzo para que puedas iterar, valorarlo o montarlo en la línea de tiempo.

La generación es el ciclo central. Coloca una toma, describe lo que quieres, elige un modelo y genera. Si el resultado no es el adecuado, ajusta y regenera. El lienzo te da espacio para comparar tomas lado a lado.`,
  },
  'generation/images': {
    source_en_hash: 'v1:2400a89jp95',
    title: 'Imágenes',
    description: 'Genera frames fijos y úsalos como referencia.',
    overview: `La generación de imágenes suele ser el punto de partida. Genera un frame fijo y luego úsalo como referencia para generar video. También puedes subir fotos, storyboards o concept art y usarlos como frames iniciales o referencias de estilo.

Las colecciones de la Library mantienen consistentes a los personajes y a los props entre tomas, anclando el modelo a una identidad visual específica. Guarda las imágenes más claras en una colección, referénciala con @ en el prompt y cada toma que la referencie mantendrá esa apariencia.`,
  },
  'generation/video': {
    source_en_hash: 'v1:1smga0lwpzx',
    title: 'Video',
    description: 'Genera tomas en movimiento a partir de prompts y referencias.',
    overview: `La generación de video convierte un prompt en una toma en movimiento. Añade un frame inicial, un frame final o una imagen de referencia cuando necesites más control. Cada modelo soporta controles distintos. Algunos aceptan frames inicial y final para movimientos de cámara precisos; otros funcionan mejor solo con texto.

La duración, la resolución y la relación de aspecto dependen del modelo. Martini mantiene la interfaz coherente para que puedas definir la intención y ver qué parámetros acepta cada modelo.`,
  },
  'generation/models': {
    source_en_hash: 'v1:1zivmdgst0h',
    title: 'Modelos',
    description: 'Cuándo usar Veo, Kling, Sora y otros.',
    overview: `Cada modelo tiene sus fortalezas. Veo se encarga del control de cámara y de la composición con frames inicial y final. Kling es rápido y muy bueno con el movimiento. Sora tiene una estética cinematográfica distintiva. Wan funciona bien con contenido estilizado. Ray genera tanto imágenes como video.

El selector de modelos muestra qué soporta cada uno: generación de video, generación de imagen o ambas. No todos los modelos aceptan todos los parámetros. Elige el modelo que se ajusta a la toma, no el que tiene más funciones. Puedes ocultar los modelos que no uses en preferencias.`,
  },
  'generation/prompting': {
    source_en_hash: 'v1:xyd9da5qjl',
    title: 'Prompting',
    description: 'Escribe prompts que produzcan resultados cinematográficos.',
    overview: `Los prompts son indicaciones, no descripciones. Escribe lo que le dirías a un DP o a un supervisor de VFX: movimiento de cámara, iluminación, ritmo y la sensación de la toma. El lenguaje concreto y físico funciona mejor que las instrucciones abstractas.

"Toma de seguimiento con cámara en mano, luz natural a través de una ventana, el sujeto se gira lentamente" es más fuerte que "una hermosa toma cinematográfica de una persona". Nombra el movimiento de cámara, la fuente de luz y la acción. Evita adjetivos como "impresionante" o "asombroso"; no se traducen en instrucciones claras para el modelo.

Antes de regenerar, cambia una parte significativa del prompt a la vez. Así es más fácil ver qué mejoró la toma.`,
  },
  'generation/upscaling': {
    source_en_hash: 'v1:1th5x70ufdk',
    title: 'Reescalado',
    description: 'Aumenta la resolución, mejora el detalle o conviértelo a HDR.',
    overview: `El escalado aumenta la resolución de una toma terminada, y también puede mejorar el detalle o convertirla a HDR. Funciona tanto en video como en imágenes. Selecciona la toma o la imagen y elige Upscale & Enhance — desde el menú contextual, el inspector, o pulsando ||U||.

![](/docs-imgs/generationUpscale.mp4)

Source: la versión de la toma sobre la que trabajar, normalmente la original. Haz clic en el icono de información para ver los detalles del original: sus dimensiones, el tipo de archivo, cuándo se creó y su ID de recurso.

Preset: un paquete guardado con todos los ajustes de abajo — modelo, resolución, calidad, codificador, etc. — conservado bajo un nombre. Custom significa que estás configurando todo a mano; una vez que tengas una combinación que te guste, haz clic en el signo más para guardarla como un preset con nombre y reutilizarla más adelante. Los presets se comparten con tu equipo, de modo que todo un proyecto puede mantener el mismo aspecto y las mismas especificaciones.

Operation: Upscale aumenta la resolución y el detalle; Enhance limpia el ruido y el desenfoque y restaura la imagen; HDR genera un archivo de alto rango dinámico de 10-bit o 16-bit.

Model: el motor que hace el trabajo. Los modelos generales como Proteus se adaptan a la mayoría de las tomas, los modelos de detalle y corrección se centran en la nitidez o en problemas específicos, y la familia Starlight ofrece la máxima calidad. El escalado de imágenes tiene sus propios modelos rápidos y generativos.

Resolution: el tamaño de salida objetivo, desde HD hasta 8K. Martini muestra el factor de escala (por ejemplo 4x) y el máximo que permite cada códec.

Quality: cuánto limpia y enfoca la imagen el modelo. Es automática de forma predeterminada — Martini elige ajustes sensatos por ti — y solo añades controles manuales (detalle, ruido, desenfoque, grano, compresión) cuando quieres un control directo sobre el aspecto.

Output: el codificador es el formato en el que se guarda el resultado — H.265, H.264 o AV1, además de los formatos de entrega HDR y ProRes. La compresión equilibra el tamaño del archivo frente a la calidad: Low conserva el máximo detalle en un archivo más grande, High hace lo contrario.

Frame rate: cuántos fotogramas se reproducen cada segundo; una tasa más alta se ve más fluida. Auto mantiene la tasa del video de origen, o establece una personalizada como 24, 30 o 60 fps.

El resultado aparece como una nueva versión en la misma pila, de modo que puedes alternar entre el original y la versión escalada para compararlos.

El costo en olives depende del modelo y del tamaño de salida: el video se factura por segundo de salida, las imágenes por megapíxel, y los modelos de mayor calidad como Starlight cuestan más.

![](/docs-imgs/upscale.png)`,
  },
  'generation/credits': {
    source_en_hash: 'v1:101i5vr0a9y',
    title: 'Olives',
    description: 'Cómo funciona el gasto de generación.',
    overview: `Los olives son la unidad de gasto de Martini. Cada generación cuesta olives. La cantidad depende del modelo, la resolución y la duración. Tu saldo está visible en el menú de cuenta.

Cuando te quedes sin olives, puedes recargar o mejorar tu plan. Si una generación falla, Martini reembolsa los olives automáticamente. La página de modelos soportados muestra el costo por modelo para que puedas planificar antes de generar.`,
  },
  'sets/overview': {
    source_en_hash: 'v1:cbl38tqd8g',
    title: 'Resumen',
    description: 'Entornos 3D construidos a partir de una sola imagen.',
    overview: `Los sets son entornos 3D construidos a partir de una sola imagen. Sube o genera una foto de referencia y Martini crea a partir de ella un espacio 3D navegable. Piensa en un set como una ubicación virtual. Puedes mover una cámara a través de él, colocar personajes y generar tomas desde ángulos específicos.

Es lo más parecido al scouting y a la construcción de un set físico. Misma ubicación, diferentes posiciones de cámara, geografía consistente entre tomas.`,
  },
  'sets/creating': {
    source_en_hash: 'v1:2enib07kray',
    title: 'Crear un set',
    description: 'Convierte una imagen en un espacio 3D navegable.',
    overview: `Empieza con una imagen generada o subida. Martini construye un entorno 3D navegable a partir de ella. El proceso tarda unos minutos.

Una vez listo, el set aparece como un nodo en tu lienzo. Haz doble clic para abrir el visor del set y navegar por el espacio 3D.`,
  },
  'sets/characters': {
    source_en_hash: 'v1:23pqljuiw5c',
    title: 'Personajes',
    description: 'Casting consistente en las tomas basadas en sets.',
    overview: `Los personajes son identidades persistentes colocadas dentro de un set. Sube o genera una hoja de personaje, y Martini detecta la figura, extrae el vestuario y crea una referencia reutilizable.

Cuando generas desde un set, el personaje aparece en el espacio 3D. Esto mantiene consistente el casting entre ángulos de cámara: misma persona, mismo vestuario, distinto encuadre.`,
  },
  'sets/viewer': {
    source_en_hash: 'v1:up8l8cp3q2',
    title: 'Visor de set',
    description: 'Navega el entorno 3D y posiciona cámaras.',
    overview: `El visor de set es un viewport 3D embebido en el lienzo. Arrastra para orbitar, desplaza para hacer zoom y arrastra con clic derecho para hacer panorámica. Estás moviendo una cámara virtual a través del entorno.

Desde cualquier posición, puedes capturar una pantalla como frame de referencia o generar una toma desde ese ángulo exacto. La posición de la cámara, los personajes y el entorno se incorporan automáticamente al prompt de generación.`,
  },
  'timeline/overview': {
    source_en_hash: 'v1:qfy9vuw7gs',
    title: 'Resumen',
    description: 'Monta tomas en una secuencia.',
    overview: `La línea de tiempo es donde las tomas se convierten en una secuencia. Arrastra tomas desde el lienzo a las pistas, ordénalas, recórtalas y previsualiza el montaje. No reemplaza a Premiere ni a DaVinci, pero es suficiente para armar un primer corte, comprobar timing y exportar un paquete para finalización.

Cambia entre el lienzo y la línea de tiempo con el conmutador de modo en la barra de herramientas. El lienzo es donde generas y organizas. La línea de tiempo es donde editas.`,
  },
  'timeline/clips': {
    source_en_hash: 'v1:ha0n4cjv11',
    title: 'Tomas y pistas',
    description: 'Añade tomas a la línea de tiempo y ordénalas en pistas.',
    overview: `Arrastra una toma desde el lienzo a la línea de tiempo para añadirla a tu montaje. Las tomas en la línea de tiempo se imantan entre sí y al cabezal de reproducción. Recorta arrastrando un borde. La línea de tiempo soporta tantas pistas como necesites.

Apila tomas en pistas distintas para hacer capas. Haz clic derecho en una toma de la línea de tiempo para ver opciones como eliminar, slip y swap.`,
  },
  'timeline/audio': {
    source_en_hash: 'v1:1hlcrir9yjf',
    title: 'Audio',
    description: 'Formas de onda, voz en off y música.',
    overview: `El audio va en sus propias pistas. Importa audio arrastrando archivos a la línea de tiempo o desde el lienzo. Las formas de onda se muestran en línea para que puedas alinear los cortes con beats o diálogos.

Las pistas de voz en off y de música funcionan igual: las sueltas y las posicionas. Los modelos que soportan generación de audio, como Veo 3, producen tomas con sonido embebido que se traslada a la línea de tiempo.`,
  },
  'timeline/playback': {
    source_en_hash: 'v1:pq7zgl6nne',
    title: 'Reproducción',
    description: 'Previsualiza tu montaje con controles de transporte.',
    overview: `||Space|| reproduce y pausa. ||J||/||K||/||L|| hacen scrub hacia atrás, detienen y avanzan. El cabezal de reproducción muestra el frame actual en el program monitor.

Mismos controles de transporte que Premiere, Avid y DaVinci. La memoria muscular se traslada.`,
  },
  'timeline/sequences': {
    source_en_hash: 'v1:mlutts77ri',
    title: 'Secuencias',
    description: 'Organiza tu montaje en escenas.',
    overview: `Las secuencias te permiten dividir la línea de tiempo en escenas. Cada secuencia es un montaje autocontenido, lo cual es útil cuando quieres trabajar en escenas individuales antes de armar el corte completo.

Cambia entre secuencias para centrarte en distintas partes del proyecto sin perder contexto. Piénsalas como rollos o actos en un montaje offline tradicional.`,
  },
  'export/overview': {
    source_en_hash: 'v1:107qvtix405',
    title: 'Resumen',
    description: 'Saca tu trabajo de Martini.',
    overview: `La exportación saca tu trabajo de Martini. Renderiza un MP4 para un video independiente, o exporta un paquete XML para finalización en Premiere, DaVinci Resolve o CapCut. El paquete XML incluye tus archivos de medios y la estructura de la línea de tiempo, así que puedes abrirlo en un editor y seguir trabajando.

Qué formato usar depende de adónde vaya el proyecto a continuación. MP4 para revisiones y entregables. XML para postproducción.`,
  },
  'export/mp4': {
    source_en_hash: 'v1:17a7qgub68u',
    title: 'MP4',
    description: 'Exportación directa de video.',
    overview: `Exporta a MP4 cuando necesites un archivo de video independiente. Elige la resolución y la calidad, y luego renderiza. La salida incluye todas las tomas de la línea de tiempo en orden, con sus puntos de recorte actuales.

Útil para primeros cortes, revisiones con clientes o redes sociales: cualquier caso en el que el video sea el entregable final.`,
  },
  'export/nle': {
    source_en_hash: 'v1:1wma3l4cdj8',
    title: 'Exportación XML',
    description: 'Exporta para Premiere, DaVinci y CapCut.',
    overview: `Exporta un paquete XML cuando quieras finalizar en Premiere Pro, DaVinci Resolve o CapCut. El paquete lleva los medios, las pistas y el timing desde tu línea de tiempo de Martini.

Usa XML para proyectos que requieren corrección de color, diseño sonoro o finalización de VFX. El XML lleva la lista de decisiones de edición. La carpeta de medios lleva las tomas.`,
  },
  'teams/overview': {
    source_en_hash: 'v1:1nwl3a18oii',
    title: 'Resumen',
    description: 'Espacios de trabajo compartidos para organizaciones.',
    overview: `Los teams son espacios de trabajo compartidos para estudios y agencias. Los miembros comparten la facturación, los ajustes de acceso a modelos y la propiedad de los proyectos. Los administradores del team controlan quién puede entrar, qué modelos están disponibles y cuánto puede gastar cada miembro.

Crea un team desde los ajustes de tu cuenta. Invita miembros por correo electrónico. Los proyectos creados dentro del team son visibles para todos sus miembros y se descuentan del saldo de olives del team.`,
  },
  'teams/members': {
    source_en_hash: 'v1:10exf22pba9',
    title: 'Miembros y roles',
    description: 'Invitaciones y permisos.',
    overview: `Invita miembros por correo electrónico. Los administradores añaden y quitan miembros, ajustan la configuración del team y controlan el gasto. Los miembros trabajan dentro de los proyectos del team y consumen de la facturación del team.

Cuando alguien deja el team, sus proyectos permanecen. No se pierde nada.`,
  },
  'teams/billing': {
    source_en_hash: 'v1:vbh19d66ga',
    title: 'Facturación del team',
    description: 'Facturación compartida y límites de gasto.',
    overview: `Los teams comparten una sola cuenta de facturación. El plan se aplica a todo el team y todos los miembros consumen del mismo saldo de olives. Los administradores pueden establecer límites de gasto por miembro para controlar costos.

El historial de facturación y las facturas muestran el gasto desglosado por miembro y por proyecto. Puedes ver exactamente adónde se fueron los olives.`,
  },
  'teams/models': {
    source_en_hash: 'v1:4buzhbqg6r',
    title: 'Gestión de modelos',
    description: 'Activa o desactiva modelos para el team.',
    overview: `Los administradores del team pueden activar o desactivar modelos de IA específicos. Si un modelo es caro o poco fiable para tu caso de uso, desactívalo. Los miembros del team no lo verán en el selector de modelos.

Esto mantiene la interfaz enfocada y los costos predecibles. Puedes cambiar la lista de modelos en cualquier momento desde los ajustes del team.`,
  },
  'account/profile': {
    source_en_hash: 'v1:19bd8xdwqco',
    title: 'Perfil',
    description: 'Tu alias y tu correo electrónico.',
    overview: `Tu alias te identifica en Martini: cursores en los proyectos, colaboración y el menú de cuenta. Tu correo electrónico se usa para iniciar sesión, recibir recibos de facturación e invitaciones a teams.

Actualiza tu perfil desde la página de ajustes de cuenta.`,
  },
  'account/preferences': {
    source_en_hash: 'v1:19yltxzw1cu',
    title: 'Preferencias',
    description: 'Modelos ocultos y ajustes de flujo de trabajo.',
    overview: `Oculta los modelos que no uses del panel de generación. Las preferencias son por cuenta y te acompañan entre proyectos y teams.

Si solo usas Veo y Kling, oculta el resto. El panel de generación queda más limpio y no tienes que pasar por modelos que nunca vas a elegir.`,
  },
  'account/billing': {
    source_en_hash: 'v1:s6ohjzhuxy',
    title: 'Facturación',
    description: 'Planes, olives, transacciones y facturas.',
    overview: `Consulta tu plan actual, saldo de olives e historial de transacciones desde la página de facturación. Mejora, reduce o recarga tu plan en cualquier momento. También puedes descargar facturas.

Los desgloses de gasto muestran los costos por proyecto para que sepas adónde se fueron tus olives. Si estás en un team, la facturación del team la gestiona por separado el administrador del team.`,
  },
  mcp: {
    source_en_hash: 'v1:1hqksqxtlfe',
    title: 'Configuración de MCP',
    description: 'Conecta agentes compatibles con MCP a proyectos de Martini.',
    overview: `Martini tiene un servidor MCP integrado que permite a los agentes trabajar dentro de tus proyectos. Conecta Claude, Cursor o tus propias herramientas para leer el estado del proyecto, crear objetos en el lienzo, lanzar generaciones y gestionar tu lienzo.

Configúralo una vez y luego automatiza tareas de producción desde cualquier agente compatible con MCP.`,
  },
}
